import React, { useState, useEffect } from 'react';
import { coins as coinsApi, images } from '../../utils/api';

const CoinSelector = ({ selectedCoinIds = [], onChange, className, id = 'coin-selector', showDetails = false }) => {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCoinForDetails, setSelectedCoinForDetails] = useState(null);
  const [sortDescending, setSortDescending] = useState(false);

  useEffect(() => {
    const fetchCoins = async () => {
      try {
        setLoading(true);
        const response = await coinsApi.getAll();
        setCoins(response);
      } catch (err) {
        setError('Failed to load coins');
        console.error('Error fetching coins:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCoins();
  }, []);

  const filteredCoins = coins
    .filter(coin => {
      const searchStr = Object.entries(coin)
        .filter(([key]) => typeof coin[key] !== 'object' && coin[key] !== null) // Exclude null values and nested objects
        .map(([_, value]) => value.toString())
        .join(' ')
        .toLowerCase();
      return searchStr.includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      const aId = a.user_coin_id || '';
      const bId = b.user_coin_id || '';
      const comparison = aId.localeCompare(bId, undefined, { numeric: true, sensitivity: 'base' });
      return sortDescending ? -comparison : comparison;
    });

  const handleCoinToggle = (coinId) => {
    const newSelection = selectedCoinIds.includes(coinId)
      ? selectedCoinIds.filter(id => id !== coinId)
      : [...selectedCoinIds, coinId];
    onChange(newSelection);
  };

  if (loading) {
    return <div className="text-gray-500">Loading coins...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  const handleRowClick = (coin, e) => {
    // Don't trigger row click if clicking on the checkbox
    if (e.target.type === 'checkbox') return;
    setSelectedCoinForDetails(coin);
  };

  return (
    <div className={className}>
      <label htmlFor={`${id}-search`} className="sr-only">Search coins</label>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          id={`${id}-search`}
          name={`${id}-search`}
          placeholder="Search coins..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
        <button
          type="button"
          onClick={() => setSortDescending(!sortDescending)}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          title={sortDescending ? 'Sort by Coin ID (A-Z)' : 'Sort by Coin ID (Z-A)'}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {sortDescending ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
            )}
          </svg>
        </button>
      </div>
      <div className="mt-2 max-h-48 overflow-y-auto border rounded-md w-full">
        {filteredCoins.length === 0 ? (
          <div className="p-2 text-gray-500 text-sm">No coins found</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredCoins.map(coin => (
              <div
                key={coin.id}
                onClick={(e) => handleRowClick(coin, e)}
                className={`flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer ${
                  selectedCoinForDetails?.id === coin.id ? 'bg-indigo-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  id={`${id}-coin-${coin.id}`}
                  name={`${id}-coin-${coin.id}`}
                  checked={selectedCoinIds.includes(coin.id)}
                  onChange={() => handleCoinToggle(coin.id)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {coin.user_coin_id ? `[${coin.user_coin_id}] ` : ''}{coin.year} {coin.denomination} {coin.mint_mark} {coin.variety}
                  {coin.grade ? ` - ${coin.grade}` : ''}{coin.certification_service_name && coin.certification_number ? ` (${coin.certification_service_name} #${coin.certification_number})` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-1 text-sm text-gray-500">
        {selectedCoinIds.length} coin{selectedCoinIds.length !== 1 ? 's' : ''} selected
      </div>

      {/* Coin Details Panel */}
      {showDetails && selectedCoinForDetails && (
        <div className="mt-4 p-4 bg-gray-50 rounded-md border">
          <div className="flex justify-between items-start mb-3">
            <h4 className="text-sm font-medium text-gray-900">Coin Details</h4>
            <button
              type="button"
              onClick={() => setSelectedCoinForDetails(null)}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex gap-4">
            {/* Left side - Details */}
            <div className="flex-1 space-y-1 text-sm">
              {selectedCoinForDetails.user_coin_id && (
                <div>
                  <span className="text-gray-500">Coin ID:</span>{' '}
                  <span className="text-gray-900 font-medium">{selectedCoinForDetails.user_coin_id}</span>
                </div>
              )}
              {selectedCoinForDetails.pcgs_number && (
                <div>
                  <span className="text-gray-500">PCGS #:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.pcgs_number}</span>
                </div>
              )}
              {selectedCoinForDetails.country && (
                <div>
                  <span className="text-gray-500">Country:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.country}</span>
                </div>
              )}
              {selectedCoinForDetails.year && (
                <div>
                  <span className="text-gray-500">Year:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.year}{selectedCoinForDetails.mint_mark ? `-${selectedCoinForDetails.mint_mark}` : ''}</span>
                </div>
              )}
              {selectedCoinForDetails.denomination && (
                <div>
                  <span className="text-gray-500">Denomination:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.denomination}</span>
                </div>
              )}
              {selectedCoinForDetails.variety && (
                <div>
                  <span className="text-gray-500">Variety:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.variety}</span>
                </div>
              )}
              {selectedCoinForDetails.minor_variety && (
                <div>
                  <span className="text-gray-500">Minor Variety:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.minor_variety}</span>
                </div>
              )}
              {selectedCoinForDetails.grade && (
                <div>
                  <span className="text-gray-500">Grade:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.grade}</span>
                </div>
              )}
              {selectedCoinForDetails.certification_number && (
                <div>
                  <span className="text-gray-500">Cert #:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.certification_number}</span>
                </div>
              )}
              {selectedCoinForDetails.purchase_date && (
                <div>
                  <span className="text-gray-500">Purchase Date:</span>{' '}
                  <span className="text-gray-900">{new Date(selectedCoinForDetails.purchase_date).toLocaleDateString()}</span>
                </div>
              )}
              {selectedCoinForDetails.purchase_price && (
                <div>
                  <span className="text-gray-500">Purchase Price:</span>{' '}
                  <span className="text-gray-900">${parseFloat(selectedCoinForDetails.purchase_price).toLocaleString()}</span>
                </div>
              )}
              {selectedCoinForDetails.purchased_from && (
                <div>
                  <span className="text-gray-500">Purchased From:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.purchased_from}</span>
                </div>
              )}
              {selectedCoinForDetails.storage_location && (
                <div>
                  <span className="text-gray-500">Location:</span>{' '}
                  <span className="text-gray-900">{selectedCoinForDetails.storage_location}</span>
                </div>
              )}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => window.open(`/coins/${selectedCoinForDetails.id}`, '_blank')}
                  className="text-indigo-600 hover:text-indigo-900 text-sm font-medium inline-flex items-center gap-1"
                >
                  View Coin
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Right side - Thumbnails (vertical layout, larger images) */}
            <div className="flex-shrink-0">
              {selectedCoinForDetails.images && selectedCoinForDetails.images.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {selectedCoinForDetails.images.slice(0, 2).map((image, idx) => {
                    const imageUrl = image.thumbnail_path 
                      ? images.getImage(image.thumbnail_path, image.source)
                      : images.getImage(image.image_path, image.source);
                    return (
                      <img
                        key={image.id || idx}
                        src={imageUrl}
                        alt={`Coin ${idx === 0 ? 'obverse' : 'reverse'}`}
                        className="w-32 h-32 object-contain rounded border bg-white"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholder.svg';
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="w-32 h-32 bg-gray-200 rounded border flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoinSelector;
